import { useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format, addDays, subDays, parseISO } from 'date-fns'
import { Settings, LayoutGrid, Calendar } from 'lucide-react'
import { useActivitiesByTour, useActivitiesRealtime, useTourAssignments, useMyTourRole } from '@/hooks/useActivities'
import { useSelectedTour } from '@/context/TourContext'
import { useOrg } from '@/context/OrgContext'
import { useAuth } from '@/context/AuthContext'
import { useViewMode } from '@/context/ViewModeContext'
import { useArtistViewMode } from '@/hooks/useArtistViewMode'
import { useNow } from '@/hooks/useNow'
import { OrgSwitcher } from '@/components/OrgSwitcher'
import { UserMenu } from '@/components/UserMenu'
import { ThemeToggle } from '@/components/ThemeToggle'
import { NotificationToggle } from '@/components/NotificationToggle'
import { ArtistViewToggle } from '@/components/ArtistViewToggle'
import { SwipeableDay } from '@/components/SwipeableDay'
import { ActivityTimeline } from '@/components/activities/ActivityTimeline'
import { getLocalDate } from '@/lib/timezones'
import { withViewTransition } from '@/lib/viewTransition'
import { computeArtistView } from '@/lib/artistView'
import { LoadingSkeleton, EmptyState, DayHero } from './TodayPage'

export default function DayPage() {
  const { date } = useParams<{ date: string }>()
  const navigate = useNavigate()
  const { activeMembership } = useOrg()
  const { user } = useAuth()
  const { mode: artistMode } = useArtistViewMode()
  const now = useNow()

  const { selectedTour: tour, loading: tourLoading } = useSelectedTour()
  const { data: activities = [], isLoading: activitiesLoading } = useActivitiesByTour(tour?.id)
  const { data: assignments } = useTourAssignments(tour?.id)
  const { data: myTourRole } = useMyTourRole(tour?.id, user?.id)
  const effectiveRole = myTourRole ?? activeMembership?.rol_nivel
  const { subscribe } = useActivitiesRealtime(tour?.id)

  useEffect(() => {
    const unsubscribe = subscribe()
    return () => { unsubscribe?.() }
  }, [tour?.id])

  const { dayActivities, dayDimmedIds } = useMemo(() => {
    const inDay = activities.filter(activity => {
      const localDate = getLocalDate(activity.start_time_utc, activity.venue_timezone)
      return localDate === date
    })
    const { visible, dimmedIds } = computeArtistView(
      inDay, effectiveRole, artistMode, assignments, user?.id
    )
    return { dayActivities: visible, dayDimmedIds: dimmedIds }
  }, [activities, date, effectiveRole, artistMode, assignments, user?.id])

  if (!date) {
    navigate('/today', { replace: true })
    return null
  }

  const isToday = date === format(new Date(), 'yyyy-MM-dd')
  const prevDay = format(subDays(parseISO(date), 1), 'yyyy-MM-dd')
  const nextDay = format(addDays(parseISO(date), 1), 'yyyy-MM-dd')

  const isLoading = tourLoading || activitiesLoading

  return (
    <div className="min-h-screen bg-background max-w-lg md:max-w-2xl lg:max-w-6xl mx-auto overflow-x-hidden">
      <header className="scroll-header sticky top-0 z-10 px-4 lg:px-8 pt-safe">
        <div className="flex items-center justify-between h-14 lg:h-16 gap-2">
          <div className="min-w-0 flex-shrink flex-grow-0">
            <OrgSwitcher />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden md:block">
              <DayPageViewToggle />
            </div>
            <ArtistViewToggle role={effectiveRole} />
            <div className="icon-cluster">
              {activeMembership?.rol_nivel === 'A' && (
                <button
                  onClick={() => navigate('/manager')}
                  aria-label="Manager dashboard"
                >
                  <Settings className="w-[18px] h-[18px]" strokeWidth={2} />
                </button>
              )}
              <NotificationToggle />
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 lg:px-8 pt-2 pb-safe">
        <div className="max-w-2xl mx-auto">
          <DayHero
            dateStr={date}
            isToday={isToday}
            tour={tour}
            activities={dayActivities}
            now={now}
            onPrev={() => withViewTransition(() => navigate(`/day/${prevDay}`), 'back')}
            onNext={() => withViewTransition(() => navigate(`/day/${nextDay}`), 'forward')}
            onJumpToday={() => navigate('/today')}
          />
        </div>

        <SwipeableDay
          key={date}
          dateKey={date}
          onPrev={() => navigate(`/day/${prevDay}`)}
          onNext={() => navigate(`/day/${nextDay}`)}
        >
          <div className="min-h-[40dvh]">
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-1 dur-base ease-out-soft">
                {!tour ? (
                  <EmptyState
                    icon="🗓️"
                    title="No active tour"
                    description="There's no active tour right now."
                  />
                ) : dayActivities.length === 0 ? (
                  <EmptyState
                    icon="✅"
                    title="Nothing scheduled"
                    description="No activities for this day."
                  />
                ) : (
                  <ActivityTimeline activities={dayActivities} dimmedIds={dayDimmedIds} />
                )}
              </div>
            )}
          </div>
        </SwipeableDay>
      </main>
    </div>
  )
}

// Same visual as the shared ViewModeToggle but navigates to /today when
// switching to week mode — DayPage can only render a single date, so week
// view has no meaning here without a detour through TodayPage's week hero.
function DayPageViewToggle() {
  const { viewMode, setViewMode } = useViewMode()
  const navigate = useNavigate()

  return (
    <button
      onClick={() => {
        if (viewMode === 'day') {
          setViewMode('week')
          navigate('/today')
        } else {
          setViewMode('day')
        }
      }}
      className="text-muted-foreground hover:text-foreground transition-colors p-2"
      aria-label={viewMode === 'day' ? 'Switch to week view' : 'Switch to day view'}
      title={viewMode === 'day' ? 'Week view' : 'Day view'}
    >
      {viewMode === 'day'
        ? <LayoutGrid className="w-5 h-5" strokeWidth={2} />
        : <Calendar className="w-5 h-5" strokeWidth={2} />}
    </button>
  )
}
